/**
 * AnalyticsTracker — SPA page-view listener for GA4.
 *
 * Mount this ONCE inside <BrowserRouter> in App.tsx. It listens to react-router's
 * location and fires trackPageView() on every virtual route change.
 *
 * Why a component (not a hook)?
 *   - It renders null so it has zero visual impact.
 *   - Placing it inside <BrowserRouter> gives it access to useLocation().
 *   - A single mount point in App.tsx ensures no double-firing.
 *
 * Note: initAnalytics() configures GA4 with send_page_view: false, so this component is the ONLY
 * sender of page views — including the first load (the effect also runs on mount).
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/src/infrastructure/lib/analytics';
import { isGuestTemplatePath } from '@/src/infrastructure/lib/template-analytics';

export function AnalyticsTracker(): null {
  const location = useLocation();

  // Keyed on pathname only: the wizard rewrites `?draftId=` after creating a draft, and that is
  // not a new page view.
  useEffect(() => {
    // Guest invitation/demo pages send their own PII-free page_view (redacted slug, fixed title,
    // template params) from GuestTemplateTracker / PublicInvitation — see template-analytics.ts.
    if (isGuestTemplatePath(location.pathname)) return;
    const handle = setTimeout(() => {
      trackPageView(location.pathname + location.search);
    }, 100);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}
